import { Dispatch } from "redux";
import { ThunkAction } from "redux-thunk";
import { XhrDeferredType } from "../../EasyProjectsCommon/General";
import { updateOperationRunner } from "../../EasyProjectsCommon/UpdateOperationRunner";
import { api, ApiActionInfo } from "./apiActions";

type ResultAction = ThunkAction<XhrDeferredType, any, any, any>;
type Options = UpdateOperationRunnerOptions;

interface UpdateOperation {
    <A1>(apiCreator: F1<A1, ApiActionInfo>): F1<F1<A1, Options> | Options, F1<A1, ResultAction>>;
    <A1, A2>(apiCreator: F2<A1, A2, ApiActionInfo>): F1<F2<A1, A2, Options> | Options, F2<A1, A2, ResultAction>>;
    <A1, A2, A3>(apiCreator: F3<A1, A2, A3, ApiActionInfo>): F1<
        F3<A1, A2, A3, Options> | Options,
        F3<A1, A2, A3, ResultAction>
    >;
}

/**
 * Send ajax requests in queue for the same operation uid.
 *
 * To create action creator with parameter use following syntax:
 *
 * let saveSomething = api.updateOperation((arg: ArgType) => ({
 *    endpoint: "api/myurl",
 *    body: { value: arg.value },
 *    ... and other ApiActionInfo properties
 * }))({ uid: "myoperationid" });
 *
 *
 * To create action with operation uid based on action creator argument use following syntax
 * ( might be used to put all updates for each entity (based on id) in queue but allow simultaneous
 * updates for entities with different ids.)
 *
 *
 * let saveSomething = api.updateOperation((arg: ArgType) => ({
 *    endpoint: "api/myurl",
 *    body: { value: arg.value },
 *    ... and other ApiActionInfo properties
 * }))(arg => ({ uid: `myoperationid${arg.id}` }));
 *
 */
export const updateOperation: UpdateOperation =
    (apiCreator: Fn<ApiActionInfo>) =>
    (operationParams: Fn<Options> | Options) =>
    (...creatorArgs: any[]) =>
    (dispatch: Dispatch<any>) => {
        let options: Options = null;
        if (typeof operationParams === "function") {
            options = operationParams(...creatorArgs);
        } else {
            options = operationParams;
        }

        // @ts-ignore
        const apiAction = api.postJson(apiCreator)(...creatorArgs);
        return updateOperationRunner.run({
            ...options,
            action: () => dispatch(apiAction),
        });
    };
